import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../Header';
import api from '../../api/axios';

const EMPTY_PROFILE = {
    employee_number: '',
    job_title: '',
    division: '',
    department: '',
    work_experience: '',
    strengths: '',
    upskilling_and_development: '',
    goals_and_aspirations: '',
    most_memorable_achievement: '',
    profile_picture_path: '',
};

export default function EmployeeProfileTeamView() {
    const { userId } = useParams();

    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(EMPTY_PROFILE);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (!userId) return;
        let alive = true;

        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get(`/api/employee-profiles/${userId}`);
                if (!alive) return;

                setUser(data.user || null);

                if (data.profile) {
                    setProfile({
                        ...EMPTY_PROFILE,
                        ...data.profile,
                    });
                } else {
                    setProfile(EMPTY_PROFILE);
                }
            } catch (e) {
                if (!alive) return;
                setError(e?.response?.data?.message || 'Failed to load employee profile.');
            } finally {
                if (alive) setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [userId]);

    const handleChange = (field, value) => {
        setProfile(prev => ({ ...prev, [field]: value }));
        setError('');
        setSuccess('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userId) return;

        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const payload = { ...profile };
            const { data } = await api.put(`/api/employee-profiles/${userId}`, payload);
            setProfile(prev => ({ ...prev, ...data.profile }));
            setSuccess('Profile saved.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to save profile.');
        } finally {
            setSaving(false);
        }
    };

    const pictureSrc =
        (profile && profile.profile_picture_path) || 'assets/images/profile.png';

    const titleName = user?.name || 'Employee';

    return (
        <>
            <Header label={<p className="py-0 my-0">Employee Profile – {titleName}</p>} />
            <main id="main" className="main">
                <div className="row">
                    <div className="col-lg-12">
                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}
                        {success && (
                            <div className="alert alert-success" role="alert">
                                {success}
                            </div>
                        )}

                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">Team Member Profile</h5>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="card-body">
                                    {loading ? (
                                        <div className="text-muted">Loading…</div>
                                    ) : (
                                        <div className="row">
                                            {/* Left column */}
                                            <div className="col-lg-4 col-md-5 mb-4">
                                                <div className="text-center mb-3">
                                                    <img
                                                        src={pictureSrc}
                                                        alt="Profile"
                                                        className="rounded-circle mb-3"
                                                        style={{ width: 120, height: 120, objectFit: 'cover' }}
                                                    />
                                                    <h5 className="mb-1">
                                                        {user?.name || 'Employee'}
                                                    </h5>
                                                    <p className="text-muted mb-1">
                                                        {profile.job_title || 'Job Title'}
                                                    </p>
                                                    <p className="text-muted small mb-0">
                                                        {user?.email}
                                                    </p>
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">Employee Number</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={profile.employee_number || ''}
                                                        onChange={e =>
                                                            handleChange('employee_number', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">Division</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={profile.division || ''}
                                                        onChange={e =>
                                                            handleChange('division', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">Department</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={profile.department || ''}
                                                        onChange={e =>
                                                            handleChange('department', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Profile Picture Path
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={profile.profile_picture_path || ''}
                                                        onChange={e =>
                                                            handleChange(
                                                                'profile_picture_path',
                                                                e.target.value,
                                                            )
                                                        }
                                                        placeholder="e.g. storage/profile-images/user.jpg"
                                                    />
                                                    <small className="text-muted">
                                                        Leave blank to use the default avatar.
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Right column */}
                                            <div className="col-lg-8 col-md-7">
                                                <div className="mb-3">
                                                    <label className="form-label">Job Title</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={profile.job_title || ''}
                                                        onChange={e =>
                                                            handleChange('job_title', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Share an Overview of Work Experience
                                                    </label>
                                                    <textarea
                                                        className="form-control"
                                                        rows={4}
                                                        value={profile.work_experience || ''}
                                                        onChange={e =>
                                                            handleChange('work_experience', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Strengths
                                                    </label>
                                                    <textarea
                                                        className="form-control"
                                                        rows={4}
                                                        value={profile.strengths || ''}
                                                        onChange={e =>
                                                            handleChange('strengths', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Upskilling and Development Areas
                                                    </label>
                                                    <textarea
                                                        className="form-control"
                                                        rows={4}
                                                        value={profile.upskilling_and_development || ''}
                                                        onChange={e =>
                                                            handleChange(
                                                                'upskilling_and_development',
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Goals and Aspirations
                                                    </label>
                                                    <textarea
                                                        className="form-control"
                                                        rows={4}
                                                        value={profile.goals_and_aspirations || ''}
                                                        onChange={e =>
                                                            handleChange(
                                                                'goals_and_aspirations',
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">
                                                        Most Memorable Achievement
                                                    </label>
                                                    <textarea
                                                        className="form-control"
                                                        rows={4}
                                                        value={profile.most_memorable_achievement || ''}
                                                        onChange={e =>
                                                            handleChange(
                                                                'most_memorable_achievement',
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="card-footer d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={saving || loading}
                                    >
                                        {saving ? 'Saving…' : 'Save Profile'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}